'use client';

import { objectFromEntries, objectKeys } from '@/utils/typedObjectHelpers';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { objectEntries } from '../utils/typedObjectHelpers';

// Generic enforces the type of this config (eg. search must be a string)
export interface TransformConfig<T> {
  fromUrl?: (value: string) => T;
  toUrl?: (value: T) => string;
  defaultValue: T;
}

// Transform config for each key in the state object
export type TypedTransformConfig<T> = {
  [K in keyof T]: TransformConfig<T[K]>;
};

// Infer the correct state type based on the keys in `transforms`
export type State<T> = {
  [K in keyof T]: T[K];
};

/**
 * Hook for managing multiple URL state parameters atomically
 *
 * @example
 * Basic table view with sorting and pagination:
 * ```tsx
 * function DataTable() {
 *   const { state, updateParams } = useUrlStateGroup({
 *     page: {
 *       fromUrl: Number,
 *       defaultValue: 1
 *     },
 *     sort: {
 *       defaultValue: 'name'
 *     },
 *     direction: {
 *       defaultValue: 'asc'
 *     }
 *   });
 *
 *   // Update multiple params at once
 *   const handleSort = (column: string) => {
 *     updateParams({
 *       sort: column,
 *       direction: state.direction === 'asc' ? 'desc' : 'asc',
 *       page: 1 // Reset to first page when sorting changes
 *     });
 *   };
 *
 *   return (
 *     <Table
 *       data={data}
 *       page={state.page}
 *       sortColumn={state.sort}
 *       sortDirection={state.direction}
 *       onSort={handleSort}
 *     />
 *   );
 * }
 * ```
 *
 * @example
 * Complex filter form with multiple parameters:
 * ```tsx
 * function FilteredList() {
 *   const { state, updateParams } = useUrlStateGroup({
 *     status: {
 *       defaultValue: 'active'
 *     },
 *     category: {
 *       defaultValue: 'all'
 *     },
 *     search: {
 *       defaultValue: ''
 *     },
 *     dateRange: {
 *       fromUrl: (val) => JSON.parse(decodeURIComponent(val)),
 *       toUrl: (val) => encodeURIComponent(JSON.stringify(val)),
 *       defaultValue: { start: null, end: null }
 *     }
 *   });
 *
 *   const handleReset = () => {
 *     updateParams({
 *       status: 'active',
 *       category: 'all',
 *       search: '',
 *       dateRange: null // Will be removed from URL
 *     });
 *   };
 *
 *   const handleFilterSubmit = (formData: any) => {
 *     updateParams({
 *       ...formData,
 *       page: 1 // Reset pagination when filters change
 *     });
 *   };
 *
 *   return (
 *     <Form
 *       values={state}
 *       onSubmit={handleFilterSubmit}
 *       onReset={handleReset}
 *     />
 *   );
 * }
 * ```
 *
 * @example
 * Using with query-based navigation:
 * ```tsx
 * function PropertySearch() {
 *   const { state, updateParams } = useUrlStateGroup({
 *     location: {
 *       defaultValue: ''
 *     },
 *     propertyType: {
 *       defaultValue: 'any'
 *     },
 *     priceRange: {
 *       fromUrl: (val) => JSON.parse(decodeURIComponent(val)),
 *       toUrl: (val) => encodeURIComponent(JSON.stringify(val)),
 *       defaultValue: { min: 0, max: 1000000 }
 *     }
 *   });
 *
 *   // All parameters will be updated in a single URL change
 *   const handleSearchSubmit = (searchCriteria: any) => {
 *     updateParams({
 *       ...searchCriteria,
 *       page: 1
 *     });
 *   };
 *
 *   return (
 *     <SearchForm
 *       criteria={state}
 *       onSubmit={handleSearchSubmit}
 *     />
 *   );
 * }
 * ```
 */
export function useUrlStateGroup<T extends object>(transforms: TypedTransformConfig<T>) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Initialize state from URL
  const initialStateMap = new Map<keyof T, T[keyof T]>();
  const keys = objectKeys(transforms);
  for (const key of keys) {
    const transform = transforms[key];
    const urlValue = searchParams?.get(key);
    const value =
      urlValue != null
        ? (transform.fromUrl?.(urlValue.toString()) ?? transform.defaultValue)
        : transform.defaultValue;
    initialStateMap.set(key, value);
  }
  const val = objectFromEntries<T>(initialStateMap);
  const [state, setState] = useState<State<T>>(val);

  useEffect(() => {
    const filteredState = Object.fromEntries(
      Object.entries(state)
        .filter(([_, v]) => v != null && v !== '')
        .map(([k, v]) => [k, String(v)]),
    );
    const query = new URLSearchParams(filteredState).toString();
    router.push(`${pathname}?${query}`);
  }, [state]);

  const updateParams = (updates: Partial<State<T>>) => {
    const newState = { ...state, ...updates };

    objectEntries(updates).forEach(([key, value]) => {
      if (value == null) {
        newState[key] = transforms[key].defaultValue;
      } else {
        newState[key] = value;
      }
    });

    setState(newState);
  };

  return { state, updateParams };
}

/**
 * Helper to parse and manage sort state from useUrlStateGroup
 *
 * Extracts common sorting logic used with FionaTable's sortOptions prop.
 * Parses the "columnKey:ASC|DESC" format from URL state and provides
 * a handler to toggle sort direction or change sort column.
 *
 * Note: This is NOT a React hook (doesn't use useState/useEffect), so it can
 * be called conditionally without violating React's rules of hooks.
 *
 * @example
 * ```tsx
 * function MyTable() {
 *   const { state, updateParams } = useUrlStateGroup({
 *     page: { fromUrl: Number, defaultValue: 1 },
 *     sortBy: { defaultValue: 'createdAt:DESC' },
 *   });
 *
 *   const { sortBy, sortOrder, handleSort } = getSortFromUrlState(state, updateParams);
 *
 *   return (
 *     <FionaTable
 *       columns={columns}
 *       data={data}
 *       sortOptions={{
 *         sortableColumns: ['name', 'createdAt'],
 *         onSort: handleSort,
 *         sortBy,
 *         sortOrder,
 *       }}
 *     />
 *   );
 * }
 * ```
 */
export function getSortFromUrlState<
  T extends { sortBy?: string; page?: number },
  K extends string = string,
>(state: T, updateParams: (updates: Partial<T>) => void) {
  const [sortBy, sortOrder] = (state.sortBy?.split(':') ?? []) as [
    K | undefined,
    'ASC' | 'DESC' | undefined,
  ];

  const handleSort = (columnKey: string) => {
    if (columnKey === sortBy) {
      updateParams({
        sortBy: `${columnKey}:${sortOrder === 'ASC' ? 'DESC' : 'ASC'}`,
        page: 1,
      } as Partial<T>);
    } else {
      updateParams({
        sortBy: `${columnKey}:ASC`,
        page: 1,
      } as Partial<T>);
    }
  };

  return { sortBy, sortOrder, handleSort };
}
